import React, { useEffect, useMemo, useRef } from 'react'
import { useTheme } from '@mui/material/styles'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Grid, Box } from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment'
import ErrorOutline from '@mui/icons-material/ErrorOutline'

import { nowOnClinicTimezone } from '@/v2/utils/convert'
import { useClinic } from '@/v2/hooks/useClinic'
import {
  range,
  fromCurrencyToNumber,
  updateContractAmounts,
} from '@/v2/utils/helpers'
import ContractField from './components/ContractField'
import InstallmentFields from './components/InstallmentFields'
import Label from '@/v2/components/common/FieldLabel'
import Typography from '@/v2/components/common/Typography'
import Divider from '@/v2/components/common/Divider'
import InputField from '@/v2/components/common/InputField'
import CurrencyInputField from '@/v2/components/common/CurrencyInputField'
import DiscountField from './components/DiscountField'
import ControlRadioField from '@/v2/components/common/ControlRadioField'
import ActionButton from '@/v2/components/common/ActionButton'
import Row from '@/v2/components/common/Row'
import { SelectCardField } from './components/SelectCardField'
import AddNewCard from '@/v2/components/common/AddNewCard'
import * as Yup from 'yup'

const PlanForm = ({ patient, state, setState, onClose }) => {
  const theme = useTheme()
  const errorContainerRef = useRef(null)
  const hydratedFromClinic = useRef(false)
  // /v2/rest/clinics.json is reachable by every clinic user (not just admins),
  // so this hydrates the Processing Fee default for the whole team. The admin-
  // only settings/general endpoint cannot be used here.
  const { clinic: clinicSettings } = useClinic()

  const contracts = (patient?.contracts || []).filter(c => c.signed && c.active)

  const PlanSchema = Yup.object()
    .shape({
      classification: Yup.string()
        .oneOf(['one_time_payment', 'installments'])
        .required('Classification is required'),

      description: Yup.string()
        .min(3, 'Description is required')
        .required('Description is required'),

      amount: Yup.string()
        // parse currency => number
        .test('amount-min', 'Amount must be greater than $0.50', value => {
          if (!value) return false
          return fromCurrencyToNumber(value) > 0.5
        })
        .required('Amount is required'),

      downPayment: Yup.number()
        .transform((value, originalValue) => {
          if (typeof originalValue === 'string') {
            return fromCurrencyToNumber(originalValue)
          }
          return value
        })
        .typeError('Initial payment must be a number')
        .min(0, 'Initial payment must not be negative')
        .notRequired(),

      interestRate: Yup.number()
        .min(0, 'Interest rate must be ≥ 0')
        .required(),
      discountRate: Yup.number()
        .min(0, 'Discount rate must be ≥ 0')
        .required(),

      amountWithDiscount: Yup.number()
        .min(0)
        .required('Amount with discount is required'),
      totalDue: Yup.number()
        .min(0)
        .required('Total due is required'),
      installmentAmount: Yup.number()
        .min(0)
        .required('Installment amount is required'),

      paymentMethodType: Yup.string()
        .oneOf(['card', 'payment_link', 'none', 'ach'])
        .required('Payment method is required'),
      paymentMethodId: Yup.string().nullable(), // can be null if not 'card'

      contractId: Yup.number().nullable(),

      installments: Yup.number()
        .min(0, 'Installments must be at least 0')
        .required('Installments is required'),

      processingFeePercentage: Yup.number()
        .transform((value, originalValue) =>
          originalValue === '' || originalValue === null ? undefined : value
        )
        .typeError('Processing fee must be a number')
        .min(0, 'Processing fee must be 0% or greater')
        .max(100, 'Processing fee must be 100% or less'),
    })
    // Cross-field #1: if contractId is set, then data.amount <= contract.totalCost
    .test(
      'contract-amount-check',
      'Amount must be ≤ contract total cost',
      function(values) {
        const { contractId, amount } = values
        if (!contractId) return true // no contract => skip
        const found = contracts.find(c => parseInt(c.id) === contractId)
        if (!found) return true
        // parse the string if needed
        const numericAmount = fromCurrencyToNumber(amount)
        return numericAmount <= found.totalCost
      }
    )
    // Cross-field #2: if paymentMethodType=card => paymentMethodId is required
    .test('card-check', 'Please select a card to pay with', function(values) {
      if (values.paymentMethodType === 'card' && !values.paymentMethodId) {
        return false
      }
      return true
    })
    .test('downPaymentChecks', null, function(values) {
      const { classification, downPayment, totalDue, installments } = values

      if (classification !== 'installments') {
        return true
      }

      const dpValue = downPayment || 0

      if (dpValue >= totalDue) {
        return this.createError({
          path: 'downPayment',
          message:
            'Initial payment should not be greater than or equal to total amount',
        })
      }

      const minInitialPayment = totalDue / installments

      if (dpValue > 0 && dpValue < minInitialPayment) {
        return this.createError({
          path: 'downPayment',
          message: `Initial payment should be at least $${minInitialPayment.toFixed(
            2
          )}`,
        })
      }

      return true
    })

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting, isSubmitSuccessful, dirtyFields },
  } = useForm({
    defaultValues: state,
    resolver: yupResolver(PlanSchema), // <-- use yupResolver
  })

  const selectedContractId = watch('contractId')
  const contract = useMemo(() => {
    if (!selectedContractId) return null
    return contracts.find(c => parseInt(c.id) === selectedContractId)
  }, [contracts, selectedContractId])

  useEffect(() => {
    if (contract) {
      setValue('amount', contract.totalCost.toString())
      setValue('description', contract.description)

      if (contract.monthlyPaymentCount > 1) {
        setValue('classification', 'installments')
      } else {
        setValue('classification', 'one_time_payment')
      }

      setValue('discountRate', contract.discountRate * 100)
      setValue('interestRate', contract.interestRate * 100)
      setValue('installments', contract.monthlyPaymentCount)

      const contractFee = contract.processingFeePercentage
      setValue(
        'processingFeePercentage',
        contractFee != null
          ? contractFee
          : clinicSettings?.processingFeePercentage ?? 0,
        { shouldDirty: true }
      )
      hydratedFromClinic.current = true

      const res = updateContractAmounts({
        amount: contract.totalCost,
        discountRate: contract.discountRate,
        interestRate: contract.interestRate,
        installments: contract.monthlyPaymentCount,
        downPayment: contract.downPayment,
      })

      setState(s => ({ ...s, updatedInstallments: res.updatedInstallments }))
      setValue('amountWithDiscount', res.amountWithDiscount)
      setValue('installmentAmount', res.installmentAmount)
      setValue('totalDue', res.totalDue)
    }
  }, [contract, setValue, setState, clinicSettings])

  // A saved paymentMethodId belongs to a specific rail (a credit card id vs a
  // bank account id), so it must not survive a rail switch — otherwise the
  // previously chosen method silently gets charged. The ref seeds from the
  // restored type so a value rehydrated on remount (e.g. after Back) is kept.
  const selectedPaymentMethodType = watch('paymentMethodType')
  const previousPaymentMethodType = useRef(state.paymentMethodType)
  useEffect(() => {
    if (previousPaymentMethodType.current === selectedPaymentMethodType) return
    previousPaymentMethodType.current = selectedPaymentMethodType
    setValue('paymentMethodId', null)
  }, [selectedPaymentMethodType, setValue])

  // On submit
  const onSubmit = async data => {
    // If valid, store data in parent state
    setState(s => ({
      ...s,
      ...data,
      processingFeeTouched:
        !!dirtyFields.processingFeePercentage || s.processingFeeTouched,
    }))
  }

  useEffect(() => {
    if (hydratedFromClinic.current || !clinicSettings) return
    // Survives Back from Confirm: if the user already edited (or cleared)
    // the fee, don't overwrite that intent with the clinic default on remount.
    if (state.processingFeeTouched) {
      hydratedFromClinic.current = true
      return
    }
    const untouched =
      state.processingFeePercentage === '' ||
      state.processingFeePercentage == null
    if (untouched) {
      setValue(
        'processingFeePercentage',
        clinicSettings.processingFeePercentage ?? 0
      )
    }
    hydratedFromClinic.current = true
  }, [
    clinicSettings,
    setValue,
    state.processingFeePercentage,
    state.processingFeeTouched,
  ])

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset()
      setState(s => ({ ...s, contract, step: 2, error: null }))
    }
  }, [isSubmitSuccessful, setState, reset, contract])

  useEffect(() => {
    if (state.error) {
      errorContainerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, [state.error])

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          width: '100%',
        }}
      >
        <ContractField
          name="contractId"
          register={register}
          errors={errors}
          contracts={contracts}
        />

        <Box display="flex" flexDirection="column" gap={2}>
          <Divider />

          <ControlRadioField
            id="classification"
            control={control}
            options={[
              { label: 'Single', value: 'one_time_payment' },
              { label: 'Installments', value: 'installments' },
            ]}
            error={errors?.classification?.message}
            name="classification"
            color="secondary"
            property="value"
            disabled={!!contract}
          />

          <InputField
            fullWidth
            label="Description"
            name="description"
            autoFocus
            placeholder="Write a description here..."
            error={errors?.description?.message}
            {...register('description')}
          />

          <Row>
            <CurrencyInputField
              label="Amount"
              name="amount"
              placeholder="$0.00"
              prefix="$"
              error={errors?.amount?.message}
              control={control}
              disabled={!!contract}
            />

            <DiscountField
              noMargin
              label="Apply discount"
              name="discountRate"
              id="discountRate"
              options={range(0, 50, 5).map(discount => ({
                label: `${discount}%`,
                value: discount,
              }))}
              color="secondary"
              errors={errors}
              register={register}
              setValue={setValue}
              control={control}
              contract={contract}
              disabled={!!contract}
            />
          </Row>

          <InstallmentFields
            errors={errors}
            register={register}
            setValue={setValue}
            control={control}
            theme={theme}
            disabled={!!contract}
            contract={contract}
            state={state}
          />
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          ref={errorContainerRef}
        >
          <Label variant="h4" bold color="grey.darkgrey" mb="5px" mt="10px">
            Payment Method
          </Label>

          {state.error && (
            <Box
              bgcolor={theme.palette.error.dark}
              color={theme.palette.common.white}
              p={1}
              display="flex"
              alignItems="center"
              gap={1}
            >
              <ErrorOutline />
              <Typography wordBreak>{state.error}</Typography>
            </Box>
          )}

          <InputField
            {...register('processingFeePercentage')}
            label="Processing Fee (Credit Card)"
            type="number"
            inputProps={{ step: '0.01', min: 0, max: 100 }}
            endAdornment={<InputAdornment position="end">%</InputAdornment>}
            error={errors?.processingFeePercentage?.message}
            fullWidth
          />

          <ControlRadioField
            id="payment-method-type"
            options={[
              { label: 'Choose Later', value: 'none' },
              { label: 'Send Payment Link', value: 'payment_link' },
              { label: 'Credit Card', value: 'card' },
              { label: 'ACH Bank Account', value: 'ach' },
            ]}
            color="secondary"
            column
            property="value"
            name="paymentMethodType"
            control={control}
            sx={{ '& label:not(:first-of-type)': { marginTop: '10px' } }}
          />

          <SelectCardField
            control={control}
            name="paymentMethodId"
            patientId={patient?.id}
            errors={errors}
          />
        </Box>

        <Divider />

        <Grid container justifyContent="flex-end" gap={2}>
          <ActionButton
            variant="text"
            color="primary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant="contained"
            color="secondary"
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Submit
          </ActionButton>
        </Grid>
      </form>

      <AddNewCard patientId={patient?.id} />
    </Box>
  )
}

export default PlanForm
